/**
 * V2.0 Layer Metadata TypeScript Interfaces
 *
 * These interfaces match the backend V2.0 schema contract exactly.
 * All layers use generic `layerMetadata` with `layerType` field for identification.
 *
 * @version 2.0
 * @created September 2025
 */

// Base interface for all layer metadata
export interface LayerMetadata {
  layerType: string;
  variantName?: string;
  baseAssetId?: string;
  [key: string]: any;
}

// Stars Layer Metadata (Complete V2.0 spec)
export interface StarsLayerMetadata extends LayerMetadata {
  layerType: 'stars';

  // Identity Fields
  starName: string;
  gender: string;
  ageGroup: string;
  archetype: string;
  energy: string;

  // Physical Traits
  hairColor: string;
  hairStyle: string;
  hairLength: string;
  hairTexture: string;
  hairTreatment: string;
  hairAccessories: string[];
  eyeColor: string;
  skinTone: string;
  bodyType: string;
  ethnicity: string;

  // Makeup & Style
  makeupType: string;
  eyeMakeup: string;
  lipMakeup: string;
  faceMakeup: string;
  makeupEffects: string[];
  colorPalette: string;

  // Performance & Personality
  personalityTraits: string[];
  careerStage: string;
  vibe: string;
  musicalStyle: string;

  // Variant Fields
  variantType?: string;
  baseStarId?: string;
}

// Songs Layer Metadata (Complete V2.0 spec)
export interface SongsLayerMetadata extends LayerMetadata {
  layerType: 'songs';

  // Core Song Fields
  songName: string;
  artistName: string;
  albumName: string;
  genre: string;
  mood: string;
  tempo: string;
  energy: string;
  bpm: number;
  key: string;
  duration: string;

  // Performance & Style
  vocalType: string;
  instrumentalType: string;
  songCulturalOrigin: string;
  releaseYear: number;
  popularity: string;

  // Technical & Metadata
  albumArt: string;
  ageAppropriateness: string[];
  originalArtistGender: string;
  genderSuitability: string;

  // Variant Fields
  variantType?: string;
  baseSongId?: string;
}

// Looks Layer Metadata (Complete V2.0 spec)
export interface LooksLayerMetadata extends LayerMetadata {
  layerType: 'looks';

  // Core Fashion Fields
  style: string;
  outfitName: string;
  styleCategory: string;
  occasion: string;
  colorScheme: string;
  colorPalette?: string;
  primaryGarment: string;
  primaryColors: string[];
  patterns: string[];
  materials: string[];

  // Context & Usage
  seasonality: string;
  timeOfDay: string;
  accessories: string[];
  jewelryType: string; // Backend expects String, not array
  brandNames: string[]; // Backend expects List<String>, max 10 items
  priceRange: string;
  energy: string;
  formality: string;

  // Variant Fields
  variantType?: string;
  baseLookId?: string;
}

// Moves Layer Metadata (Complete V2.0 spec)
export interface MovesLayerMetadata extends LayerMetadata {
  layerType: 'moves';

  // Core Dance Fields
  danceStyle: string;
  energy: string;
  complexity: string;
  moveName: string;
  choreographerName: string;

  // Performance Characteristics
  danceClassification: Record<string, unknown>;
  performanceCharacteristics?: Record<string, unknown>; // Optional, removed from API
  energyTempo: Record<string, unknown>;
  movementPatterns: string[];
  musicCompatibility?: Record<string, unknown>; // Optional, removed from API
  culturalContext: Record<string, unknown>;

  // Variant Fields
  variantType?: string;
  baseMoveId?: string;
}

// Worlds Layer Metadata (Complete V2.0 spec)
export interface WorldsLayerMetadata extends LayerMetadata {
  layerType: 'worlds';

  // Core Environment Fields
  theme: string;
  atmosphere: string;
  setting: string;
  location: string;
  timePeriod: string;
  lighting: string;
  spatialSize: string;
  seasonality: string;
  weatherConditions: string;

  // Context & Style
  culturalContext: Record<string, unknown>;
  architecturalStyle: string;
  colorPalette: string;
  moodAtmosphere: Record<string, unknown>;

  // Variant Fields
  variantType?: string;
  baseWorldId?: string;
}

// Composites Layer Metadata (Complete V2.0 spec)
export interface CompositesLayerMetadata extends LayerMetadata {
  layerType: 'composites';

  // Core Composite Fields
  compositeType: string;
  baseAssetId: string;
  variantType: string;
  compositeName: string;
  assetType: string;
  baseLookId: string;
  variantName: string;

  // Style & Characteristics
  brandInfo: Record<string, unknown>;
  styleClassification: Record<string, unknown>;
  visualCharacteristics: Record<string, unknown>;
  contextUsage: Record<string, unknown>;
  physicalProperties: Record<string, unknown>;
  accessories: Record<string, unknown>;
  energyLevel: string;
  quality: string;
}

// Union type for all layer metadata
export type AnyLayerMetadata =
  | StarsLayerMetadata
  | SongsLayerMetadata
  | LooksLayerMetadata
  | MovesLayerMetadata
  | WorldsLayerMetadata
  | CompositesLayerMetadata;

// Type guards for layer identification
export function isStarsLayerMetadata(
  metadata: LayerMetadata
): metadata is StarsLayerMetadata {
  return metadata.layerType === 'stars';
}

export function isSongsLayerMetadata(
  metadata: LayerMetadata
): metadata is SongsLayerMetadata {
  return metadata.layerType === 'songs';
}

export function isLooksLayerMetadata(
  metadata: LayerMetadata
): metadata is LooksLayerMetadata {
  return metadata.layerType === 'looks';
}

export function isMovesLayerMetadata(
  metadata: LayerMetadata
): metadata is MovesLayerMetadata {
  return metadata.layerType === 'moves';
}

export function isWorldsLayerMetadata(
  metadata: LayerMetadata
): metadata is WorldsLayerMetadata {
  return metadata.layerType === 'worlds';
}

export function isCompositesLayerMetadata(
  metadata: LayerMetadata
): metadata is CompositesLayerMetadata {
  return metadata.layerType === 'composites';
}

// Helper function to get layer-specific metadata
export function getLayerMetadata<T extends LayerMetadata>(
  metadata: LayerMetadata,
  layerType: string
): T | null {
  if (metadata.layerType === layerType) {
    return metadata as T;
  }
  return null;
}

// Default values for each layer type
export const DEFAULT_STARS_METADATA: Partial<StarsLayerMetadata> = {
  layerType: 'stars',
  starName: 'Unknown',
  gender: 'Unknown',
  ageGroup: 'Unknown',
  archetype: 'Unknown',
  energy: 'Unknown',
  hairColor: 'Unknown',
  hairStyle: 'Unknown',
  hairLength: 'Unknown',
  hairTexture: 'Unknown',
  hairTreatment: 'Unknown',
  hairAccessories: [],
  eyeColor: 'Unknown',
  skinTone: 'Unknown',
  bodyType: 'Unknown',
  ethnicity: 'Unknown',
  makeupType: 'Unknown',
  eyeMakeup: 'Unknown',
  lipMakeup: 'Unknown',
  faceMakeup: 'Unknown',
  makeupEffects: [],
  colorPalette: 'Unknown',
  personalityTraits: [],
  careerStage: 'Unknown',
  vibe: 'Unknown',
  musicalStyle: 'Unknown',
};

export const DEFAULT_SONGS_METADATA: Partial<SongsLayerMetadata> = {
  layerType: 'songs',
  songName: 'Unknown Song',
  artistName: 'Unknown Artist',
  albumName: 'Unknown Album',
  genre: 'Pop',
  mood: 'Energetic',
  tempo: 'Medium',
  energy: 'Medium',
  bpm: 120,
  key: 'C Major',
  duration: '3:30',
  vocalType: 'Unknown',
  instrumentalType: 'Unknown',
  songCulturalOrigin: 'Unknown',
  releaseYear: 2025,
  popularity: 'Unknown',
  albumArt: '',
  ageAppropriateness: ['all-ages'],
  originalArtistGender: 'Unknown',
  genderSuitability: 'All',
};

// Export default values and constants
export default {
  DEFAULT_STARS_METADATA,
  DEFAULT_SONGS_METADATA,
};
